<template>
  <div class="multiselect-dropdown" ref="dropdown">
    <!-- Label -->
    <label v-if="label" class="multiselect-label" :for="name">{{
      label
    }}</label>

    <!-- Selected items & toggle -->
    <div :class="['multiselect-control', { 'has-error': validationError }]" @click="toggleDropdown">
      <div class="selected-items">
        <template v-if="selectedItems.length">
          <div
            v-for="(item, idx) in visibleSelected"
            :key="item.id || idx"
            class="selected-item"
            :title="getLabel(item)"
          >
            <span>{{ getLabel(item) }}</span>
            <button
              v-if="multiple"
              class="remove-item"
              type="button"
              @click.stop="removeSelected(item)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div
            v-if="multiple && restCount > 0"
            class="selected-item more"
            @click.stop="dropdownOpen = true"
          >
            +{{ restCount }}
          </div>
        </template>
        <h5 v-else class="placeholder">{{ label }}</h5>
      </div>

      <button class="toggle-button" type="button">
        <i :class="dropdownOpen ? 'fas fa-angle-up' : 'fas fa-angle-down'"></i>
      </button>
    </div>
<span v-if="validationError" class="error-message">{{ validationError }}</span>


    <!-- Dropdown options -->
    <ul v-show="dropdownOpen" class="dropdown-options" @scroll="onScroll">
      <!-- Search -->
      <div class="search-wrap">
        <i class="fas fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="Lang.get('messages.search')"
          @input="resetVisibleCount"
        />
      </div>

      <li
        v-for="option in filteredOptions"
        :key="option.id || option[labelField]"
      >
        <input
          v-if="multiple"
          type="checkbox"
          :id="'option' + (option.id ?? getLabel(option))"
          :checked="isSelected(option)"
          @change="toggleOption(option)"
        />
        <input
          v-else
          type="radio"
          name="single-select"
          :id="'option' + (option.id ?? getLabel(option))"
          :checked="isSelected(option)"
          @change="toggleOption(option)"
        />
        <label
          :for="'option' + (option.id ?? getLabel(option))"
          class="option-label"
          >{{ getLabel(option) }}</label
        >
      </li>
    </ul>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: "MultiSelectDropdown",
  props: {
    label: String,
    name: { type: String, required: true },
    model: { type: [Object, Array], required: true },
    options: { type: Array, required: true },
    labelField: { type: String, required: true },
    valueField: String,
    multiple: { type: Boolean, default: false },
    reduce: { type: Function, default: (v) => v },
    error: { type: String, default: "" }, 
  errors: { type: Object, default: () => ({}) }, 
  required: { type: Boolean, default: false }
  },
  data() {
    return {
      dropdownOpen: false,
      searchQuery: "",
      visibleCount: 5, // initial batch
    };
  },
  computed: {
    currentValue() {
      return _.get(this.model, this.name);
    },
    selectedItems() {
      const value = this.currentValue;
      if (this.multiple) {
        if (Array.isArray(value)) {
          // reduced primitives -> map to objects
          if (value.length && typeof value[0] !== "object") {
            return value.map((v) => this.getOptionByValue(v)).filter(Boolean);
          }
          return value;
        }
        return [];
      } else {
        if (!value) return [];
        if (typeof value !== "object") {
          const opt = this.getOptionByValue(value);
          return opt ? [opt] : [];
        }
        return [value];
      }
    },
    visibleSelected() {
      const max = this.calculateMaxVisibleItems();
      return this.selectedItems.slice(0, max);
    },
    restCount() {
      const max = this.calculateMaxVisibleItems();
      return this.selectedItems.length > max
        ? this.selectedItems.length - max
        : 0;
    },
    filteredOptions() {
      const q = this.searchQuery.toLowerCase();
      const all = q
        ? this.options.filter((o) => this.getLabel(o).toLowerCase().includes(q))
        : this.options;
      return all.slice(0, this.visibleCount);
    },
     validationError() {
  // 1. If individual error prop is provided, show it
  if (this.error) return this.error;

  // 2. If API/server errors exist for this field, show it
  // Support nested field names like "yearLevel.stage"
  const keys = this.name.split('.');
  let serverError = this.errors;
  for (const key of keys) {
    if (!serverError) break;
    serverError = serverError[key];
  }
  if (serverError && typeof serverError === 'string') return serverError;
  if (Array.isArray(serverError) && serverError.length) return serverError[0];

  // 3. Required field front-end check
  const value = this.currentValue;

// if (this.required) {
//     if (this.multiple) {
//       if (!Array.isArray(value) || value.length === 0) {
//         return `${this.label || 'This field'} is required.`;
//       }
//     } else {
//       if (!value) {
//         return `${this.label || 'This field'} is required.`;
//       }
//     }
//   }
  return '';


}

  },
  methods: {
    getLabel(option) {
      return option[this.labelField] || option.label || option.name || "";
    },
    getOptionByValue(val) {
      const key = this.valueField || "id";
      return this.options.find(
        (o) => o && typeof o === "object" && (o[key] === val || o.id === val)
      );
    },
    isSelected(option) {
      const value = this.currentValue;
      const reduced = this.reduce(option);
      if (this.multiple) {
        if (!Array.isArray(value)) return false;
        if (value.length && typeof value[0] !== "object") {
          return value.includes(reduced);
        }
        return value.some(
          (item) =>
            (item?.id && option?.id && item.id === option.id) || item === option
        );
      } else {
        if (!value) return false;
        if (typeof value !== "object") return value === reduced;
        return (
          (value.id && option.id && value.id === option.id) || value === option
        );
      }
    },
    toggleOption(option) {
      const currentValue = this.currentValue;
      let newValue;
      if (this.multiple) {
        const currentArray = Array.isArray(currentValue)
          ? [...currentValue]
          : [];
        const reduced = this.reduce(option);
        if (currentArray.length && typeof currentArray[0] !== "object") {
          newValue = currentArray.includes(reduced)
            ? currentArray.filter((v) => v !== reduced)
            : [...currentArray, reduced];
        } else {
          if (this.isSelected(option)) {
            newValue = currentArray.filter(
              (item) =>
                !(item?.id && option?.id && item.id === option.id) &&
                item !== option
            );
          } else {
            newValue = [...currentArray, option];
          }
          newValue = newValue.map((i) => this.reduce(i));
        }
      } else {
        newValue = this.reduce(option);
        this.dropdownOpen = false;
      }
      this.updateModel(newValue);
      this.$emit("change", newValue);
    },
    removeSelected(option) {
      const currentValue = this.currentValue;
      let newValue;
      if (this.multiple) {
        const currentArray = Array.isArray(currentValue)
          ? [...currentValue]
          : [];
        if (currentArray.length && typeof currentArray[0] !== "object") {
          const reduced = this.reduce(option);
          newValue = currentArray.filter((v) => v !== reduced);
        } else {
          newValue = currentArray.filter(
            (item) =>
              !(item?.id && option?.id && item.id === option.id) &&
              item !== option
          );
          newValue = newValue.map((i) => this.reduce(i));
        }
      } else {
        newValue = null;
      }
      this.updateModel(newValue);
      this.$emit("change", newValue);
    },
    updateModel(value) {
      if (this.name.includes(".")) {
        _.set(this.model, this.name, value);
      } else {
        this.$set(this.model, this.name, value);
      }
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) this.resetVisibleCount();
    },
    calculateMaxVisibleItems() {
      const width = this.$refs.dropdown ? this.$refs.dropdown.offsetWidth : 140;
      return Math.floor(width / 140) || 1;
    },
    onScroll(e) {
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
        this.visibleCount += 5;
      }
    },
    resetVisibleCount() {
      this.visibleCount = 5;
    },
    handleClickOutside(e) {
      if (!this.$refs.dropdown.contains(e.target)) {
        this.dropdownOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.multiselect-dropdown {
  width: 100%;
  position: relative;
  margin-bottom: 1rem;
}

.multiselect-label {
  display: block;
  font-size: 1rem;
  margin-bottom: 4px;
  color: var(--dark-gray-text);
}

.multiselect-control {
  display: flex;
  align-items: center;
  height: 40px;
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  background: #fff;
  padding: 6px 8px;
  position: relative;
  cursor: pointer;
}

.placeholder {
  font-size: 1rem;
  color: var(--dark-gray-text);
}

.selected-items {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  flex: 1;
  align-items: center;
}

.selected-item {
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  padding: 0 6px;
  max-width: 140px;
  display: flex;
  align-items: center;
}
.selected-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.selected-item.more {
  cursor: pointer;
}

.remove-item {
  background: transparent;
  border: none;
  cursor: pointer;
}
.remove-item i {
  color: red;
  transition: color 0.2s;
}
.remove-item:hover i {
  color: darkred;
}

.toggle-button {
  background: transparent;
  border: none;
}

.dropdown-options {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  width: 100%;
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  z-index: 99;
  max-height: 200px; /* scroll container */
  overflow-y: auto;
  padding: 8px;
}

.search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e4e4e4;
  padding: 4px 8px;
  border-radius: 5px;
  margin-bottom: 8px;
}
.search-wrap input {
  border: none;
  width: 100%;
  outline: none;
}

.dropdown-options li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.dropdown-options li input {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.option-label {
  cursor: pointer;
  font-size: 14px;
  color: var(--dark-gray-text) !important;
  opacity: 0.8;
  font-style: italic;
}
.option-label:hover {
  opacity: 1;
  font-style: normal;
}
.error-message {
  display: block;
  color: #dc3545!important;
  font-size: 0.85rem;
  margin-top: 4px;
}

</style>
